import type { FieldValues, Path } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import {
  useEffect, useMemo, useRef,
} from 'react';
import { areDifferent, omitProperties } from 'flow/service/util';
import debounce from 'lodash/debounce';

interface UseNodeFormAutoSaveProps<T extends FieldValues> {
  fn: (data: T) => void;
  ignoreFields?: Path<T>[];
  debounceTimeMs?: number;
}

/**
 * Handles the mechanics of invoking the passed `fn` when the form state changes,
 * expect fields optionally defined in `ignoreFields`.
 * Our form implementation would cause repeated invocations of the `fn` function
 * so this hook guarantees that it only happens when the data changed.
 * `fn` is debounced (configurable via `debounceTimeMs`) to not fire potentially
 * expensive actions for every keystroke.
 */
export default function useNodeFormAutoSave<T extends FieldValues>({
  fn,
  ignoreFields = [],
  debounceTimeMs = 500,
}: UseNodeFormAutoSaveProps<T>) {
  const { watch, handleSubmit, getValues } = useFormContext<T>();

  // only watch the fields that are not ignored.
  const fieldsToWatch = Object.keys(
    omitProperties(getValues(), ignoreFields),
  ) as Path<T>[];

  const formState = watch(fieldsToWatch);
  const prevFormState = useRef(formState);
  const isInitialized = useRef(false);

  const debouncedFn = useDebouncedFunction({ fn, debounceTimeMs });
  useEffect(() => () => debouncedFn.cancel(), [debouncedFn]);

  useEffect(() => {
    /**
     * our form implementation forces the initial form data to be an empty object,
     * so we opt out of the first render cycle of this hook
     */
    if (!isInitialized.current) {
      isInitialized.current = true;
      return;
    }
    if (prevFormState.current.length > 0 && areDifferent(prevFormState.current, formState)) {
      // the form state is provided by the `handleSubmit` implementation
      handleSubmit(debouncedFn)();
    }
    prevFormState.current = formState;
  }, [debouncedFn, formState, handleSubmit]);


  return null;
}


/**
 * Debounces a function with optional params
 */
export function useDebouncedFunction<T>(args: {
  fn: (data: T) => void;
  debounceTimeMs?: number;
}) {
  const {
    fn,
    debounceTimeMs = 500,
  } = args;

  return useMemo(
    () => debounce((fs: T) => fn(fs), debounceTimeMs), [fn, debounceTimeMs],
  );
}
